import { ChangeDetectorRef, Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { handleFormatDate } from 'app/core/shared/helper/date.helper';
import { saveAs } from 'file-saver';
import { WellProductionService } from 'app/modules/well-production-status/services/well-production-status.service';
import { EditWellProdModalComponent } from 'app/modules/well-production-status/component/edit-well-prod-modal/edit-well-prod-modal.component';
import { HistoricalDataModalComponent } from 'app/modules/well-production-status/component/historical-data-modal/historical-data-modal.component';
import { EMPTY, map, merge, Subject, switchMap, takeUntil } from 'rxjs';
import module from "app/modules/well-production-status/well-production-status.module";
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { DEFAULT_COLUMN_LABELS } from 'app/core/shared/constant/column-label';
import { ApplySortPayload } from 'app/core/shared/component/sort-menu/sort-menu.component';
import { UserService } from 'app/core/user/user.service';

@Component({
  selector: 'app-well-production-status',
  templateUrl: './index.page.html',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: module,
  styleUrls: ['./index.page.scss']
})
export class WellProdIndexPage {
  @ViewChild(MatPaginator) private _paginator: MatPaginator;
  @ViewChild(MatSort) private _sort: MatSort;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  searchForm: UntypedFormGroup;
  dt: any
  startingIndex: number = 0
  isLoading: boolean = false

  sortInfo: string;
  private columnLabels: { [key: string]: string } = {
    ...DEFAULT_COLUMN_LABELS,
    wellFlowCategory: 'Flow Category',
    wellStringType: 'Well Type',
    lastUpdatedBy: 'Last Updated By',
    lastUpdatedDate: 'Last Updated Date',
    wellProductionStatus: 'Current Production Status',
  };

  columnSort = Object.entries(this.columnLabels).map(([key, name]) => ({
    key,
    name,
    isChecked: false, // default unchecked
  }));

  constructor(
    private dialog: MatDialog,
    private wellProdServices: WellProductionService,
    private _changeDetectorRef: ChangeDetectorRef,
    private userService: UserService,
    private _formBuilder: UntypedFormBuilder) {
  }

  ngOnInit() {
    this.setSearch();
    this.setDataTable();
    this.setPage({ offset: 0 });
  }

  ngAfterViewInit() {
    if (this._sort && this._paginator) {
      // Set the initial sort
      this._sort.sort({
        id: 'field',
        start: 'asc',
        disableClear: true,
      });

      // Mark for check
      this._changeDetectorRef.markForCheck();

      // If the user changes the sort order...
      this._sort.sortChange
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(() => {
          // Reset back to the first page
          this._paginator.pageIndex = 0;

          this.onSort(this._sort)
        });


      // Get platform types if sort or page changes
      merge(this._sort.sortChange, this._paginator.page).pipe(
        switchMap(() => {
          // this.isLoading = true;

          this.startingIndex = this._paginator.pageIndex * this._paginator.pageSize;

          let pageInfo = {
            'count': this._paginator.length,
            'pageSize': this._paginator.pageSize,
            'limit': this._paginator.pageSize,
            'offset': this._paginator.pageIndex
          }
          this.setPage(pageInfo); // Emit page number

          return EMPTY
        }),
        map(() => {
          this.isLoading = false;
        }),
      ).subscribe();
    }
  }


  editProdCategoryModal(stringData: any) {
    const dialogRef = this.dialog.open(EditWellProdModalComponent, {
      data: stringData, // Pass your parameters here
      width: "70%"
    });

    dialogRef.afterClosed().subscribe(isSuccess => {
      if (isSuccess) {
        this.setPage({ offset: this.dt.page.offset });
      }
    });
  }

  openHistoricalModal(wellDetails) {
    const dialogRef = this.dialog.open(HistoricalDataModalComponent, {
      data: wellDetails,
      width: "70%"
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log(`Dialog result: ${result}`);
    });
  }

  handleFormatDate(dateString: string, withTime) {
    return handleFormatDate(dateString, withTime)
  }

  exportToExcel() {
    this.wellProdServices.downloadWellProdCategories().subscribe((res) => {
      saveAs(res, 'Well Production Status');
    })
  }

  // 
  // Method for Table
  // 

  handleSearch(event$) {
    const field = event$?.Field;
    const platform = event$?.Platform;
    const well = event$?.Well;
    const string = event$?.String;

    if ([field, platform, well, string].some(v => v != null && v !== '')) {
      this.setPage({ offset: 0 });
    }
  }

  setSearch() {
    this.sortInfo = 'Field'
    this.searchForm = this._formBuilder.group({
      Page: [0],
      PageSize: [20],
      OrderBy1: [null],
      OrderBy2: [null],
      OrderBy3: [null],
      OrderDir: 'asc',
      WellName: [''],
      PlatformName: [''],
      StringName: [''],
      FieldName: [''],
      SortColumns: this._formBuilder.array(
        this.columnSort.map(col => this._formBuilder.group(col))
      ) // col already has {key, name, isChecked}
    });

    this.searchForm.get('PageSize').valueChanges.subscribe(value => this.setSize(value));
  }

  setSize(size: number) {
    this.dt.page.size = size;
    this.dt.page.offset = 0
    this.setPage({ offset: this.dt.page.offset });
  }

  setPage(pageInfo) {
    this.dt.page.offset = pageInfo.offset;
    this.startingIndex = this.dt.page.offset * this.dt.page.size

    this.searchForm.patchValue({
      Page: this.dt.page.offset + 1,
    });

    const params = this.searchForm.value;

    const search: any = {
      wellLongName: params.Well?.wellLongName,
      stringShortName: params.String?.stringShortName,
      platformLongName: params.Platform?.platformLongName,
      fieldLongName: params.Field?.fieldLongName,
    };

    this.wellProdServices
      .getWellProdCategories({ ...params, search })
      .subscribe((pagedData) => {
        this.dt.page.totalPages = pagedData.data.totalPages;
        this.dt.page.totalRecords = pagedData.data.totalRecords;
        this.dt.rows = pagedData.data.data;
      });
  }

  setDataTable() {
    this.dt = {
      page: {
        size: this.searchForm.get('PageSize')?.value,
        totalRecords: 0,
        totalPages: 0,
        offset: 0,
      },
      rows: [],
    };
  }

  onSort(sort: any) {
    if (!sort) return;
    this.searchForm.patchValue({
      OrderBy1: sort.active,
      OrderDir: sort.direction,
    });


    this.sortInfo = this.columnLabels[sort.active] || sort.active;
    this.setPage({ offset: 0 });
  }

  reset() {
    this.setSearch();
    this.setPage({ offset: 0 });
  }

  applySort(payload: ApplySortPayload) {
    // Patch form (fill missing with null)
    const [OrderBy1, OrderBy2, OrderBy3] = [
      payload.columns[0]?.key ?? null,
      payload.columns[1]?.key ?? null,
      payload.columns[2]?.key ?? null,
    ];

    this.searchForm.patchValue({
      OrderBy1,
      OrderBy2,
      OrderBy3,
      OrderDir: payload.orderDir ?? 'asc',
    });

    const labels = payload.columns.map(k => k.name);
    this.sortInfo = labels.length ? labels.join(' → ') : 'No sorting';
    this.setPage({ offset: 0 });
  }

  hasWellProdStatusEditRoles(): boolean {
    // roles that can edit cdft
    let hasRole = false;

    const roles = new Set([
      'Well Integrity Desk Engineer',
      'Well Integrity Engineer',
    ]);

    this.userService.roles$
      .subscribe((userRoles) => {
        hasRole = userRoles.some((r) => roles.has(r));
      })
      .unsubscribe();
    return hasRole;
  }
}
